import { tw } from "@careinfo/tailwind";
import React from "react";
import { classNames } from "../../../utils/classNames";
import Template from "../Datatable/loading";

interface TrialBalanceEntry {
  name: string;
  children: any;
  credit: number;
  debit: number;
  opening_credit_balance: any;
  opening_debit_balance: any;
  closing_balance: any;
  balance: number;
}

interface TrialBalanceCategory {
  [categoryName: string]: TrialBalanceEntry[];
}

interface TrialBalanceData {
  results: TrialBalanceCategory;
  isLoading: boolean;
}

interface TableProps {
  data: any;
}

const TableRow: React.FC<{ entry: TrialBalanceEntry; level?: number }> = ({
  entry,
  level = 0,
}) => (
  <>
    <div
      className={classNames(
        level === 0 ? tw` bg-gray-200  font-semibold` : tw` font-medium`,
        tw`grid grid-cols-4 p-2 border-t border-[#dcdcdc] bg-white`
      )}
    >
      <span
        className={tw`justify-self-start`}
        style={{ paddingLeft: `${level * 20}px` }}
      >
        {entry.name}
      </span>

      <div className={tw`flex justify-around items-center`}>
        <span>
          {Number(entry?.opening_debit_balance).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </span>
        <span>
          {Number(entry?.opening_credit_balance).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </span>
      </div>
      <div className={tw`flex justify-around items-center`}>
        <span>
          {Number(entry?.debit).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
        <span>
          {Number(entry?.credit).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>
      <div className={tw`flex justify-around items-center`}>
        <span>
          {entry?.closing_balance < 0
            ? Math.abs(entry?.closing_balance).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00"}
        </span>
        <span>
          {entry?.closing_balance > 0
            ? Math.abs(entry?.closing_balance).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00"}
        </span>
      </div>
    </div>

    {entry.children?.map((child) => (
      <TableRow key={child.name} entry={child} level={level + 1} />
    ))}
  </>
);

const Table: React.FC<TableProps> = ({ data }) => {
  const flattenEntries = (entries: any[]): TrialBalanceEntry[] =>
    entries.flatMap((entry) => [
      entry,
      ...(entry.children ? flattenEntries(entry.children) : []),
    ]);

  const topLevelEntries: TrialBalanceEntry[] = Object.values(
    data
  ).flat() as TrialBalanceEntry[];

  const getLeafEntries = (
    entries: TrialBalanceEntry[]
  ): TrialBalanceEntry[] => {
    return entries.flatMap((entry) =>
      entry.children && entry.children.length > 0
        ? getLeafEntries(entry.children)
        : [entry]
    );
  };

  const leafEntries = getLeafEntries(topLevelEntries);

  const totalCredit = topLevelEntries.reduce(
    (sum, item) => sum + item.credit,
    0
  );

  const totalDebit = topLevelEntries.reduce((sum, item) => sum + item.debit, 0);

  const totalClosingCredit = leafEntries.reduce(
    (sum, item) => sum + (item?.closing_balance > 0 ? item.closing_balance : 0),
    0
  );

  const totalClosingDebit = leafEntries.reduce(
    (sum, item) =>
      sum + (item?.closing_balance < 0 ? Math.abs(item.closing_balance) : 0),
    0
  );

  const totalOpeningDebit = topLevelEntries.reduce(
    (sum, item) => sum + (item?.opening_debit_balance || 0),
    0
  );

  const totalOpeningCredit = topLevelEntries.reduce(
    (sum, item) => sum + (item?.opening_credit_balance || 0),
    0
  );

  return (
    <div className={tw`border border-[#dcdcdc] rounded-md overflow-hidden`}>
      {/* Header */}
      <div className={tw`grid grid-cols-4 bg-primary text-white p-2`}>
        <span className={tw`justify-self-start`}>Account Name</span>
        <div className={tw`text-center`}>
          <span>Opening</span>
          <div className={tw`flex justify-around`}>
            <span>Debit</span>
            <span>Credit</span>
          </div>
        </div>
        <div className={tw`text-center`}>
          <span>Periodical</span>
          <div className={tw`flex justify-around`}>
            <span>Debit</span>
            <span>Credit</span>
          </div>
        </div>
        <div className={tw`text-center`}>
          <span>Closing</span>
          <div className={tw`flex justify-around`}>
            <span>Debit</span>
            <span>Credit</span>
          </div>
        </div>
      </div>

      {data.map((categoryEntry) => (
        <React.Fragment key={categoryEntry.name}>
          <TableRow entry={categoryEntry} />
        </React.Fragment>
      ))}
      <div className={tw`grid grid-cols-4 bg-gray-200 font-semibold p-2`}>
        <span className={tw`justify-self-start`}>Total</span>
        <div className={tw`flex justify-around `}>
          <span>
            {totalOpeningDebit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span>
            {totalOpeningCredit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>
            {totalDebit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span>
            {totalCredit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <div className={tw`flex justify-around`}>
          <span>
            {totalClosingDebit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span>
            {totalClosingCredit.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

const TrialBalanceTable: React.FC<{
  trialBalanceData: TrialBalanceData;
  isLoading: boolean;
}> = ({ trialBalanceData, isLoading }) => {
  return (
    <div className={tw`space-y-4`}>
      {isLoading ? (
        <Template addNew={""} customSearch={""} noOfColumns={10} />
      ) : (
        <Table data={trialBalanceData?.results} />
      )}
    </div>
  );
};

export default TrialBalanceTable;
